/**
 * Hoja 01 del paquete: la PORTADA.
 *
 * Es la misma hoja que el contador firma en el Excel, y por eso lleva lo que
 * lleva esa hoja: razón social, título, período, moneda, el índice de estados,
 * la nota legal y el pie de firmas. Que el membrete sea configurable no sirve de
 * nada si la pantalla no muestra lo que va a salir impreso.
 *
 * Debajo van las magnitudes y las identidades contables con su diferencia real.
 * Un paquete que cuadra «de memoria» es exactamente el problema que esta
 * plataforma sustituye: aquí se comprueba de un vistazo, sin abrir once hojas.
 *
 * No repite el estado de homologación: ese vive ahora en cada pestaña de la
 * barra, que es donde se necesita —al ir a la hoja— y no en una lista aparte.
 */

import { Link } from "react-router-dom";
import { StatTile } from "../../components/StatTile";
import { StatusBadge } from "../../components/StatusBadge";
import { formatMillions, formatMoney } from "../../components/format";
import { isBalanced, useClosing } from "./closingContext";

export function ClosingOverview() {
  const { package: pkg } = useClosing();
  const m = pkg.magnitudes;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Portada</h1>
          <p>
            {pkg.cover.company} · {pkg.cover.period_text} ·{" "}
            {pkg.cover.currency_note}
          </p>
        </div>
      </div>

      <div className="card portada">
        <p className="portada-titulo">{pkg.cover.title}</p>
        <ol className="portada-indice">
          {pkg.cover.index.map((entrada) => (
            <li key={entrada}>{entrada}</li>
          ))}
        </ol>
        <p className="portada-separador">{pkg.cover.separator}</p>
        <p className="portada-nota">{pkg.cover.legal_note}</p>
        {/* El pie de firmas se muestra tal como se imprime —nombre, inscripción
            y cargo—, para que quien configura el membrete vea el resultado sin
            tener que descargar el PDF y compararlo. */}
        <div className="portada-firmas">
          {pkg.cover.signatures.map((firma) => (
            <div className="portada-firma" key={firma.name}>
              <b>{firma.name}</b>
              {firma.registration && <span>{firma.registration}</span>}
              <span className="portada-cargo">{firma.role}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="tiles">
        <StatTile
          label="Total activo"
          value={formatMillions(m.ESF_ACTIVO_TOTAL)}
          note={formatMoney(m.ESF_ACTIVO_TOTAL)}
          mass
        />
        <StatTile
          label="Total pasivo"
          value={formatMillions(m.ESF_PASIVO_TOTAL)}
          note={formatMoney(m.ESF_PASIVO_TOTAL)}
          mass
        />
        <StatTile
          label="Patrimonio"
          value={formatMillions(m.ESF_PATRIMONIO)}
          note={formatMoney(m.ESF_PATRIMONIO)}
          mass
        />
        <StatTile
          label="Resultado del ejercicio"
          value={formatMoney(m.ERI_RESULTADO)}
          note={`sobre ingresos de ${formatMoney(m.ERI_TOTAL_INGRESOS)}`}
          mass
        />
      </div>

      <div className="card">
        <h2>Comprobaciones</h2>
        <div className="rows">
          {pkg.checks.map((check) => (
            <div className="row-item" key={check.id}>
              <div className="row-key">
                <b>{check.label}</b>
                <span>diferencia {formatMoney(check.difference)}</span>
              </div>
              <div className="row-val">
                <StatusBadge tone={isBalanced(check.difference) ? "good" : "critical"}>
                  {isBalanced(check.difference) ? "Correcto" : "Descuadre"}
                </StatusBadge>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>Reglas de presentación vigentes</h2>
        {pkg.disclosures.length === 0 && (
          <p className="muted small">Ninguna regla aplica en este período.</p>
        )}
        <div className="rows">
          {pkg.disclosures.map((rule) => (
            <div className="row-item" key={rule.rule_id}>
              <div className="row-key">
                <b>
                  <span className="mono">{rule.rule_id}</span> {rule.text || rule.reason}
                </b>
                <span>
                  vigente desde {rule.period_from}
                  {rule.period_to ? ` hasta ${rule.period_to}` : ""} · efecto neto{" "}
                  {formatMoney(rule.net_effect)}
                </span>
              </div>
              <div className="row-val">
                {formatMoney(rule.gross_amount)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {pkg.notes.some((note) => note.status !== "resuelto") && (
        <div className="card">
          <h2>Observaciones abiertas</h2>
          <div className="rows">
            {pkg.notes
              .filter((note) => note.status !== "resuelto")
              .map((note) => (
                <div className="row-item" key={note.id}>
                  <div className="row-key">
                    <b>
                      <span className="mono">{note.id}</span> {note.title}
                    </b>
                    <span>
                      {note.responsible} · afecta a {note.affects}
                    </span>
                  </div>
                  <div className="row-val">
                    <StatusBadge tone="serious">{note.status}</StatusBadge>
                  </div>
                </div>
              ))}
          </div>
          <p className="small muted" style={{ marginTop: 10 }}>
            <Link to="/closing/notes">Ver todas las notas</Link>
          </p>
        </div>
      )}
    </>
  );
}
